import React from 'react';
import { theme } from '../../theme/theme.ts';

type Props = {
  width?: number;
  coverage: number;
};

/**
 * Lightweight SVG pie chart replacing @mui/x-charts PieChart.
 * Renders a circle background (unfulfilled) with an arc overlay (coverage).
 */
export const SystemCardPieChart: React.FC<Props> = React.memo(
  ({ width = 28, coverage }) => {
    const size = width;
    const r = Math.max(0, size / 2 - 1);
    const cx = size / 2;
    const cy = size / 2;

    const bgColor = theme.palette.primary.main;
    const fgColor = theme.palette.info.light;

    // Edge cases
    if (coverage <= 0) {
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cy} r={r} fill={bgColor} />
        </svg>
      );
    }

    if (coverage >= 1) {
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cy} r={r} fill={fgColor} />
        </svg>
      );
    }

    // SVG arc for the coverage portion
    const angle = coverage * 2 * Math.PI;
    // Start at top (12 o'clock), go clockwise
    const startX = cx;
    const startY = cy - r;
    const endX = cx + r * Math.sin(angle);
    const endY = cy - r * Math.cos(angle);
    const largeArc = coverage > 0.5 ? 1 : 0;

    const arcPath = [
      `M ${cx} ${cy}`,
      `L ${startX} ${startY}`,
      `A ${r} ${r} 0 ${largeArc} 1 ${endX} ${endY}`,
      'Z',
    ].join(' ');

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill={bgColor} />
        <path d={arcPath} fill={fgColor} />
      </svg>
    );
  },
);

SystemCardPieChart.displayName = 'SystemCardPieChart';
